/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Inject, Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import * as _ from 'underscore';
import { AppConfigService } from '@app/common/app-config.service';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { HttpHeaders } from '@angular/common/http';
import { MessageService } from '@core/lib/services/message.service';
import { WorkList } from '@app/data-model/worklist-navigator/work-list/work-list';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { UtilService } from '@app/util/util.service';

const log = new Logger('UserPreferenceService');

@Injectable({
  providedIn: 'root'
})
export class UserPreferenceService {
  static userLevelPreferenceChanged = false;

  private defaultWorkListObserver$: BehaviorSubject<string> = new BehaviorSubject('');
  public userDefaultWorklist: WorkList | null = null;
  public userDefaultDepartment: string;
  private document: Document;
  private window: Window;
  private userPreferenceCache = new Map();
  private userLevelPreferenceCache = new Map();
  private registeredListeners = new Map();
  private autoAdvanceList = [];
  private autoAdvancePreferenceValue = 'none';
  private readonly userPrefsKeys =
    'dictate,unassign-on-signout,unassign-on-tab-closure,viewer,unsigned-filter,' +
    'clear-inline-filter,autoadvance,highlight-sircc-terms,groupByPatient,screen-mode,' +
    'save-report-on-tab-closure,show-ai-findings,ptHistoryConf,autoLaunchViewerFromExternalSystem,' +
    'autoMinimizeDictation,associableExamTab,save-report-on-release-control,nativeAcademicWorkflow,' +
    'apply-viewer-done-on-tab-closure,sr-department,colorAssigntoColumn,showArrivedExamsDifferentColor';

  private _themeObserver$: Subject<boolean> = new Subject<boolean>();
  private srDeptSet = false;
  private _srDeptObserver$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.srDeptSet);
  private loadingFromCache = false;

  userPreferences = {
    dictate: false,
    autoLaunchViewerFromExternalSystem: false,
    autoAdvanceActive: false,
    autoAdvanceAssigned: false,
    groupByPatient: false,
    'unassign-on-signout': 'decide',
    'unassign-on-tab-closure': 'decide',
    'save-report-on-release-control': 'save',
    'save-report-on-interrupt': false,
    'screen-mode': false,
    'save-report-on-tab-closure': false,
    'apply-viewer-done-on-tab-closure': false,
    showAiFindings: false,
    hideCancelledExams: false,
    hideScheduledExams: false,
    hideOrderedExams: false,
    autoMinimizeDictation: true,
    associableExamTab: true,
    colorAssigntoColumn: false,
    nativeAcademicWorkflow: false,
    showArrivedExamsDifferentColor: false
  };
  displayDefaultWorklist: any;
  worklistNames: any = [];
  srDefaultDepartment = '';
  constructor(
    private translate: TranslateService,
    private notify: NotifyService,
    private appConfigService: AppConfigService,
    private dataService: ProxyDataService,
    private messageService: MessageService,
    @Inject(DOCUMENT) document: any,
    private utilService: UtilService
  ) {
    this.document = document;
    this.window = this.document.defaultView;
    this.userLevelPreferenceCache.set('userLevelPreference', {});
    this.broadcastListeners();
  }

  setEncryptedCredentials() {
    if (
      !this.utilService.externalLoginApp &&
      (this.utilService.getSystemProp()['SSO_Flag'] === undefined ||
        this.utilService.getSystemProp()['SSO_Flag'] === 'false')
    ) {
      this.dataService.setEncryptedCredentials();
    }
  }

  /* Default Worklist */
  getDefaultWorklistUpdateObserver(): Observable<string> {
    return this.defaultWorkListObserver$.asObservable();
  }

  setUserDefaultWorklist(worklist: WorkList): void {
    this.userDefaultWorklist = worklist;
  }

  getUserDefaultWorklist() {
    return this.userDefaultWorklist;
  }

  updateDefaultWorklist(newDefaultWorklist, currentDefaultWorklistId) {
    const requestBody = { newDefaultWlId: newDefaultWorklist.worklistId, currentWorklistId: currentDefaultWorklistId };
    return new Promise((resolve, reject) => {
      this.dataService
        .update(appConstants.appURLs.defaultUserWorkList, newDefaultWorklist.worklistId, requestBody)
        .then(
          (data: any) => {
            this.defaultWorkListObserver$.next(newDefaultWorklist.id);
            this.setUserDefaultWorklist(newDefaultWorklist);
            resolve(data);
          },
          error => {
            log.error(
              'UserPreferenceService::: Error occurred while receiving worklist data Error=> ' + JSON.stringify(error)
            );
            reject(error);
          }
        );
    });
  }

  getDefaultUserWorklist() {
    return new Promise((resolve, reject) => {
      this.dataService.get(appConstants.appURLs.defaultUserWorkList).then(
        (data: any) => {
          resolve(data);
        },
        error => {
          log.error(
            'UserPreferenceService::: Error occurred while receiving ' +
              'defaultUserWorklist data Error=> ' +
              JSON.stringify(error)
          );
          reject(error);
        }
      );
    });
  }

  getUserPreferenceByKey() {
    const prefKeys = this.userPrefsKeys.split(',');
    this.loadingFromCache = true;

    prefKeys.forEach(key => {
      const preferenceValue = this.getUserPreferenceFromCache(key);
      if (preferenceValue === undefined) {
        this.setUserPreferences(key, undefined);
      } else {
        this.setUserPreferences(key, String(preferenceValue));
      }
      // TO-DO service code for this method need to be updated.
      // UserPreferenceService.onUserPreferenceUpdate(key, _.partial(this.setUserPreferences, key, _), $scope);
    });

    this.loadingFromCache = false;
  }

  // Update or create the user preference for the logged in user.
  // It is to update the user preference property
  // when user click on it, if property not exists corresponds to the user in Database, create an entry.
  updateUserPreference(preferenceName, preferenceValue) {
    if (preferenceValue === undefined || preferenceValue === '') {
      preferenceValue = this.userPreferences[preferenceName];
      preferenceValue = preferenceValue.toString();
    }

    const userPreference$ = this.createOrUpdateUserPreference(preferenceName, preferenceValue);
    userPreference$.then(
      _data => {
        if (_data) {
          log.debug('User preference updated.');
          this.setUserPreferences(preferenceName, preferenceValue);
          // Broadcast to refresh the active worklist whenever user changes the groupByPatient Preference.
          if (preferenceName === 'ptHistoryConf') {
            this.messageService.broadcast('filterPtHisOnToggle', preferenceValue);
          }
          const activeList = this.appConfigService.getActiveList();
          if (preferenceName === 'groupByPatient') {
            if (
              activeList.isGlobal ||
              (!activeList.searchCriteriaInfo && activeList.displayName !== 'recentExams.recentExamsLabel')
            ) {
              this.messageService.broadcast('list.display', { list: activeList });
            }
          }
          if (preferenceName === 'colorAssigntoColumn' || preferenceName === 'showArrivedExamsDifferentColor') {
            if (activeList) {
              this.messageService.broadcast('list.refresh', { worklist: activeList });
            }
          }
        }
      },
      _error => {
        log.error('UserPreferenceController: ' + JSON.stringify(_error));
      }
    );
  }

  // set user preference options
  setUserPreferences(preferenceKey, preferenceValue) {
    if (preferenceKey === 'unassign-on-signout' || preferenceKey === 'unassign-on-tab-closure') {
      if (preferenceValue === undefined) {
        preferenceValue = 'decide';
      }
      this.userPreferences[preferenceKey] = preferenceValue;
    } else if (preferenceKey === 'save-report-on-release-control') {
      if (preferenceValue === undefined) {
        // preference value - default discard ico; not defined in DB
        preferenceValue = 'discard';
      }
      this.userPreferences[preferenceKey] = preferenceValue;
    } else if (preferenceKey === 'autoadvance') {
      this.autoAdvance(preferenceValue);
    } else if (preferenceKey.indexOf('ptHistory') >= 0) {
      this.ptHistory(preferenceKey, preferenceValue);
    } else if (preferenceKey === 'show-ai-findings') {
      this.setAIUserPreference(preferenceValue);
    } else {
      this.userPreferences[preferenceKey] = preferenceValue === 'true';
    }

    !this.loadingFromCache && this.messageService.broadcast(`userPreferenceUpdated::${preferenceKey}`, preferenceValue);
  }

  ptHistory(preferenceKey, preferenceValue) {
    if (preferenceKey === 'ptHistoryConf') {
      preferenceValue = preferenceValue === undefined ? 'none' : preferenceValue;
      //update patient history list and preferences while loading from db
      this.updatePtHistoryConf(preferenceValue);
    } else {
      preferenceValue = preferenceValue === undefined ? false : preferenceValue;
      //update patient image history body parts
      this.updatePtHistoryFilter(preferenceKey, preferenceValue);
    }
    return preferenceValue;
  }

  //Update the preferenceValues with the latest changes.
  updatePtHistoryPreference() {
    const preferenceValues = [];
    let index = 0;
    if (this.userPreferences['hideCancelledExams']) {
      preferenceValues[index] = 10;
      index++;
    }
    if (this.userPreferences['hideOrderedExams']) {
      preferenceValues[index] = 20;
      index++;
    }
    if (this.userPreferences['hideScheduledExams']) {
      preferenceValues[index] = 30;
    }

    if (preferenceValues.toString() === '') {
      this.updateUserPreference('ptHistoryConf', 'none');
    } else {
      this.updateUserPreference('ptHistoryConf', preferenceValues.toString());
    }
  }

  setAIUserPreference(preferenceValue) {
    this.userPreferences.showAiFindings = preferenceValue === 'true' || preferenceValue === true;
    this.messageService.broadcast(
      'isSiteLevelUserPrefAIEnabledEvent',
      this.getAiFindingsEnable() && this.userPreferences.showAiFindings
    );
  }

  //update the userpreferences while the user login
  updatePtHistoryConf(preferenceValue) {
    const values = preferenceValue.split(',');
    this.userPreferences['hideCancelledExams'] = _.contains(values, '10');
    this.userPreferences['hideOrderedExams'] = _.contains(values, '20');
    this.userPreferences['hideScheduledExams'] = _.contains(values, '30');
  }

  updatePtHistoryFilter(preferenceKey, preferenceValue) {
    this.userPreferences[preferenceKey] = preferenceValue === true || preferenceValue === 'true';
  }

  autoAdvance(preferenceValue) {
    if (preferenceValue === undefined) {
      preferenceValue = 'none';
    }
    // update autoadvance list and preferences while loading from db
    this.updateAutoAdvance(preferenceValue);
    return preferenceValue;
  }

  updateAutoAdvance(preferenceValue) {
    switch (preferenceValue) {
      case 'activeWorklistAndAssigned':
        this.autoAdvanceList[0] = 'activeWorklist';
        this.autoAdvanceList[1] = 'assigned';
        this.userPreferences['autoAdvanceActive'] = true;
        this.userPreferences['autoAdvanceAssigned'] = true;
        break;
      case 'assigned':
        this.autoAdvanceList[0] = 'assigned';
        this.userPreferences['autoAdvanceActive'] = false;
        this.userPreferences['autoAdvanceAssigned'] = true;
        break;
      case 'activeWorklist':
        this.autoAdvanceList[0] = 'activeWorklist';
        this.userPreferences['autoAdvanceActive'] = true;
        this.userPreferences['autoAdvanceAssigned'] = false;
        break;
      default:
        this.userPreferences['autoAdvanceActive'] = false;
        this.userPreferences['autoAdvanceAssigned'] = false;
        break;
    }
  }

  autoAdvanceToggle(autoAdvanceCheck) {
    this.messageService.broadcast('autoAdvanceUserPreferenceChanged');
    if (this.autoAdvanceList.indexOf(autoAdvanceCheck) === -1) {
      this.autoAdvanceList.push(autoAdvanceCheck);
    } else {
      this.autoAdvanceList.splice(this.autoAdvanceList.indexOf(autoAdvanceCheck), 1);
    }
    this.updateAutoAdvanceUserPreference();
  }

  updateAutoAdvanceUserPreference() {
    const checkAutoAdvance = this.autoAdvanceList.length;
    switch (checkAutoAdvance) {
      case 1:
        if (this.autoAdvanceList[0] === 'assigned') {
          this.autoAdvancePreferenceValue = 'assigned';
        } else {
          this.autoAdvancePreferenceValue = 'activeWorklist';
        }
        break;
      case 2:
        this.autoAdvancePreferenceValue = 'activeWorklistAndAssigned';
        break;
      default:
        this.autoAdvancePreferenceValue = 'none';
        break;
    }
    this.updateUserPreference('autoadvance', this.autoAdvancePreferenceValue);
  }

  getUserPreference(key?: string) {
    return new Promise((resolve, reject) => {
      if (key === 'userLevelPreference') {
        if (!_.isEmpty(this.userLevelPreferenceCache.get('userLevelPreference'))) {
          resolve(this.userLevelPreferenceCache.get('userLevelPreference'));
        } else {
          // IE 9 caches old response data, need to initiate new request using timestamp when login
          this.dataService
            .httpGet(appConstants.appURLs.userPreference + '?timestamp=' + new Date().getTime(), {
              params: { prefKeys: key }
            })
            .then(
              (data: any) => {
                log.info('Inside new getUserPreference service for key ' + key + ' received ' + data);
                if (!_.isEmpty(data)) {
                  this.userLevelPreferenceCache.set('userLevelPreference', data);
                }
                resolve(this.userLevelPreferenceCache.get('userLevelPreference'));
              },
              error => {
                log.error(
                  'UserPreferenceService::: Error occurred while receiving preference data Error=> ' +
                    JSON.stringify(error)
                );
                this.notify.warning(this.translate.instant('userPreference.warningToastMsg'));
                resolve(this.userLevelPreferenceCache.get('userLevelPreference'));
              }
            );
        }
      } else {
        const userPrefKey = [];
        const userPrefObj = new Map();
        const prefkeys = this.userPrefsKeys.split(',');

        for (let i = 0; i < prefkeys.length; i++) {
          userPrefObj.set(prefkeys[i], this.userPreferenceCache.get(prefkeys[i]));
          if (userPrefObj.get(prefkeys[i]) === undefined) {
            userPrefKey[i] = prefkeys[i];
          }
        }

        if (userPrefKey.length > 0) {
          log.info('Inside getUserPreference service if user preference object from cache is undefined');
          this.dataService
            .httpGet(appConstants.appURLs.userPreference + '?timestamp=' + new Date().getTime(), {
              params: { prefKeys: userPrefKey.join() }
            })
            .then(
              (data: any) => {
                // Put user preference object from server into cache
                for (const prefKey of prefkeys) {
                  this.userPreferenceCache.set(prefKey, data[prefKey]);
                  if (data[prefKey] === undefined) {
                    this.notifyListeners(prefKey, undefined);
                  } else {
                    this.notifyListeners(prefKey, data[prefKey].value);
                  }
                }

                log.debug('Inside getUserPreference service => cached user preference object');

                if (prefkeys.length > 1) {
                  resolve(data);
                } else {
                  if (data[key] === undefined) {
                    resolve(undefined);
                  } else {
                    resolve(data[key].value);
                  }
                }
              },
              error => {
                log.error(`UserPreferenceService::: Error occurred while receiving preference data
                Error=>${JSON.stringify(error)}`);
                reject(error);
              }
            );
        } else {
          if (userPrefObj[key]) {
            // User preference value retrieved from cache if cache is define
            resolve(userPrefObj[key].value);
          } else {
            resolve(undefined);
          }
        }
      }
    });
  }

  getUserPreferenceFromCache(key) {
    try {
      let value = this.userPreferenceCache.get(key).value;
      if ((key === 'unassign-on-signout' || key === 'unassign-on-tab-closure') && value === undefined) {
        value = 'decide';
      }
      if (key === 'save-report-on-release-control' && value === undefined) {
        value = 'save';
      }
      if (value === 'true' || value === 'false') {
        value = value === 'true';
      }
      return value;
    } catch (error) {
      log.debug('Error occurred in retrieving data from the cache: ' + error);
      return false;
    }
  }

  getUserLevelPreferenceFromCache(key) {
    try {
      const userLevelPreference = this.userLevelPreferenceCache.get('userLevelPreference');
      if (key === 'All') {
        return userLevelPreference;
      } else {
        return userLevelPreference[key];
      }
    } catch (error) {
      log.debug('Error occurred in retrieving data from userLevelPreference cache: ' + error);
      return false;
    }
  }

  setUserPreferenceToCache(key, value) {
    const userPrefCacheObj = this.userPreferenceCache.get(key);
    if (userPrefCacheObj) {
      userPrefCacheObj.value = value;
      this.userPreferenceCache.set(key, userPrefCacheObj);
    }
  }

  savePreferences(prefObj) {
    return new Promise((resolve, reject) => {
      const url = appConstants.appURLs.userPreference + 'save';
      const preferences = [];
      _.each(prefObj, (value, key) => {
        const userPrefObj = this.userPreferenceCache.get(key);
        if (!userPrefObj) {
          // Throws an error if get user preference is not invoked before save request
          log.error(
            'UserPreferenceService::: Error occurred while calling saveMultiUserPreference() => ' +
              'User Preference object ' +
              key +
              ' not initialized'
          );
        } else {
          userPrefObj.uuid = this.appConfigService.getUserId();
          userPrefObj.value = value;
          preferences.push(userPrefObj);
        }
      });
      if (preferences.length > 0) {
        const httpOptions = {
          headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' })
        };
        this.dataService.httpPost(url, preferences, httpOptions).then(
          data => {
            if (data) {
              for (const i in preferences) {
                if ({}.hasOwnProperty.call(preferences, i)) {
                  // Update cached entry for logged in user
                  this.userPreferenceCache.set(preferences[i].key, preferences[i]);
                  this.notifyListeners(preferences[i].key, preferences[i].value);
                }
              }
              resolve(data);
            } else {
              // If call is not successful to service, throw an error
              log.error(
                'UserPreferenceService::: Error occurred while calling createOrUpdateUserPreference() => ' +
                  'User Preference object ' +
                  _.keys(prefObj) +
                  ' could not be created/updated through REST API'
              );
              reject(
                'User Preference object ' + _.keys(prefObj) + ' could not be created/updated ' + 'through REST API'
              );
            }
          },
          error => {
            log.error(
              'UserPreferenceService::: Error occurred while sending preference data => ' + JSON.stringify(error)
            );
            reject(error);
          }
        );
      } else {
        reject('User Preference object not initialized');
      }
    });
  }

  saveUserPreference(key, value) {
    return new Promise((resolve, reject) => {
      const url = appConstants.appURLs.userPreference + 'save';
      const userPreference = [];
      const userPrefObj = {
        uuid: this.appConfigService.getUserId(),
        value: JSON.stringify(value),
        key
      };
      userPreference.push(userPrefObj);
      if (value) {
        const httpOptions = {
          headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' })
        };
        this.dataService.httpPost(url, userPreference, httpOptions).then(
          data => {
            if (data) {
              resolve(data);
            } else {
              // If call is not successful to service, throw an error
              log.error('UserPreferenceService::: Error occurred while calling saveUserPreference()');
              reject('User Preference object could not be created/updated through REST API');
            }
          },
          error => {
            log.error(
              'UserPreferenceService::: Error occurred while saveUserPreference() data  => ' + JSON.stringify(error)
            );
            reject(error);
          }
        );
      } else {
        reject('User Preference object not initialized');
      }
    });
  }

  createOrUpdateUserPreference(key, value) {
    const prefObj = {};
    prefObj[key] = value;
    return this.savePreferences(prefObj);
  }

  createOrUpdateUserLevelPreference(UserLevelPref, immediateSave = false) {
    UserPreferenceService.userLevelPreferenceChanged = true;
    const keys = _.keys(UserLevelPref);
    const userLevelPreferenceReference = this.userLevelPreferenceCache.get('userLevelPreference');
    _.each(keys, key => {
      userLevelPreferenceReference[key] = UserLevelPref[key];
      this.messageService.broadcast(`userLevelPreferenceUpdated::${key}`, UserLevelPref[key]);
    });

    if (immediateSave) {
      return this.saveAllUserPreferences();
    }

    this.throttledSaveAllUserPreferences();
  }

  throttledSaveAllUserPreferences() {
    _.throttle(() => {
      this.saveAllUserPreferences();
    }, 10000);
  }

  saveAllUserPreferences() {
    if (UserPreferenceService.userLevelPreferenceChanged) {
      this.saveUserPreference('userLevelPreference', this.userLevelPreferenceCache.get('userLevelPreference')).then(
        () => {
          UserPreferenceService.userLevelPreferenceChanged = false;
        }
      );
    }
  }

  onUserPreferenceUpdate(userPreferenceKey, callback) {
    if (Array.isArray(this.registeredListeners.get(userPreferenceKey))) {
      this.registeredListeners.get(userPreferenceKey).push(callback);
    } else {
      this.registeredListeners.set(userPreferenceKey, [callback]);
    }
  }

  getAiFindingsEnable(): boolean {
    return this.appConfigService.getAppConfigByKey('ai.findings.enabled') === 'ON';
  }

  isShowAiFindingsEnabled() {
    return this.getUserPreferenceFromCache('show-ai-findings') === true && this.getAiFindingsEnable();
  }

  private isWorklistDeletedOrPrivlegeRemoved(worklistId) {
    if (this.userDefaultWorklist?.id.toString() === worklistId.toString()) {
      this.userDefaultWorklist = null;
    }
  }

  broadcastListeners() {
    this.messageService.on('allWorklistRefresh').subscribe(worklists => {
      this.worklistNames[1] = worklists;
      this.displayDefaultWorklist = this.worklistNames;
    });

    this.messageService.on('customWorklistRefresh').subscribe(customWorklists => {
      this.worklistNames[0] = _.where(customWorklists, { isLinked: false });
      this.displayDefaultWorklist = this.worklistNames;
    });

    this.messageService.on('worklist.deleted').subscribe(worklistId => {
      this.isWorklistDeletedOrPrivlegeRemoved(worklistId);
    });
  }

  private notifyListeners(key, value) {
    if (this.registeredListeners.get(key)) {
      for (const listener of this.registeredListeners.get(key)) {
        listener.call(null, value);
      }
    }
  }

  applyTheme() {
    const lightsMode = !!this.getUserLevelPreferenceFromCache('LightsMode');

    const masterStyle = (lightsMode && 'master-lights-on.css') || 'master-lights-off.css';
    const themeStyle = (lightsMode && 'lights-on.css') || 'lights-off.css';

    let link: HTMLLinkElement = this.document.getElementById('master-wfm-lights-theme') as HTMLLinkElement;

    // change link references only if it is different,
    // to not trigger a style reaload of the already loaded style.
    if (link.href.indexOf(masterStyle) < 0) {
      link.href = masterStyle;

      link = this.document.getElementById('wfm-lights-theme') as HTMLLinkElement;
      link.href = themeStyle;
    }

    this.window['GEHC'].EDS.theme.setTheme((lightsMode && 'eds-lights-on') || 'eds-lights-off');
    this._themeObserver$.next(lightsMode);
  }

  setUserLogLevel() {
    const logLevel = this.getUserLevelPreferenceFromCache('LogLevel');
    if (logLevel && window['appConfig'].logLevel && window['appConfig'].logLevel > logLevel) {
      window['appConfig'].logLevel = logLevel;
      window['appConfig'].logToServer = true;
    }
  }

  get themeObserver$(): Subject<boolean> {
    return this._themeObserver$;
  }

  setSRDefaultDepartment(srDefaultDepartment) {
    const userPreference =
      this.userLevelPreferenceCache.get('userLevelPreference')[appConstants.USER_PREFERENCE_KEYS.SR_DEPARTMENT];
    if (userPreference === undefined) {
      this.userLevelPreferenceCache.get('userLevelPreference')[appConstants.USER_PREFERENCE_KEYS.SR_DEPARTMENT] =
        srDefaultDepartment;
    }
    this.srDeptSet = true;
    this._srDeptObserver$.next(this.srDeptSet);
  }

  getSRDeptObserver(): Observable<boolean> {
    return this._srDeptObserver$.asObservable();
  }

  getSRDepartment() {
    return this.getUserLevelPreferenceFromCache(appConstants.USER_PREFERENCE_KEYS.SR_DEPARTMENT);
  }

  isArrivedExamsBackgroundEnabled() {
    return this.getUserPreferenceFromCache('showArrivedExamsDifferentColor');
  }
}
